import React, { useState, useMemo, useCallback, memo } from "react";
import { AHSeparator, Box, ListItem, EmployeeAvatar, Dialog } from "arkh";

import { FormattedMessage } from "react-intl";

import { graphql, useFragment, usePaginationFragment } from "react-relay";

import { useEvent } from "widgets/src/useEvent";
import { onPressInHaptic } from "widgets/src/onPressInHaptic";
import { FlashList } from "@shopify/flash-list";
import { TVAssigneesPopover_assignable$key } from "./__generated__/TVAssigneesPopover_assignable.graphql";
import { TVAssigneesPopover_EmployeeItem$key } from "./__generated__/TVAssigneesPopover_EmployeeItem.graphql";

import { TVAssigneesPopover_assignableEmployee$key } from "./__generated__/TVAssigneesPopover_assignableEmployee.graphql";
import { TVAssigneesPopover_roles$key } from "./__generated__/TVAssigneesPopover_roles.graphql";
import { RoleListItem } from "widgets/src/RoleListItem/RoleListItem";

type AssigneesPopoverProps = {
  setAssignEmployeeIds: (assignEmployeeIds: string[]) => void;
  assignable: TVAssigneesPopover_assignable$key;
  disabled?: boolean;
  onClose: () => void;
  visible: boolean;
  roles?: TVAssigneesPopover_roles$key | null;
  assignedRoleIds?: string[];
  setAssignRoleIds?: (roleIds: string[]) => void;
  rolesLocked?: boolean;
};

export function AssigneesPopover(props: AssigneesPopoverProps) {
  const { setAssignEmployeeIds } = props;

  const _assignable = useFragment<TVAssigneesPopover_assignable$key>(
    graphql`
      fragment TVAssigneesPopover_assignable on Assignable {
        assigned {
          employee {
            id
            ...TVAssigneesPopover_EmployeeItem
          }
        }

        ...TVAssigneesPopover_assignableEmployee
      }
    `,
    props.assignable
  );

  const roles = useFragment(
    graphql`
      fragment TVAssigneesPopover_roles on EmployeeRole @relay(plural: true) {
        id
        name
        ...RoleListItem_role
      }
    `,
    props.roles ?? null
  );

  const { data, loadNext, hasNext, isLoadingNext } = usePaginationFragment(
    graphql`
      fragment TVAssigneesPopover_assignableEmployee on Assignable
      @argumentDefinitions(
        cursor: { type: "String" }
        count: { type: "Int", defaultValue: 15 }
      )
      @refetchable(queryName: "TVAssigneesPopoverPaginationQueryNonWorking") {
        assignableEmployees(after: $cursor, first: $count)
          @connection(key: "TVAssigneesPopover_assignableEmployees") {
          edges {
            node {
              id
              name
              ...TVAssigneesPopover_EmployeeItem
              shifts {
                status
                scheduleType {
                  title
                }
              }
            }
          }
        }
      }
    `,
    _assignable as TVAssigneesPopover_assignableEmployee$key
  );

  const loadMore = useCallback(() => {
    if (hasNext && !isLoadingNext) {
      loadNext(20);
    }
  }, [hasNext, isLoadingNext, loadNext]);

  const [_assignedShowedOnTop] = useState(_assignable);
  const assignedShoedOnTop = _assignedShowedOnTop.assigned.flatMap((a) =>
    a.employee
      ? [
          {
            employee: a.employee,
            type: "employee" as const,
            id: a.employee.id + "assigned",
          },
        ]
      : []
  );

  const assignedEmployeeIds = useMemo(() => {
    return _assignable.assigned.flatMap((assigned) =>
      assigned.employee?.id ? [assigned.employee.id] : []
    );
  }, [_assignable.assigned]);

  const rest = data.assignableEmployees.edges.flatMap((edge) =>
    edge.node && !assignedShoedOnTop.some((e) => e.employee.id === edge.node.id)
      ? [
          {
            employee: edge.node,
            type: "employee" as const,
            id: edge.node.id + "rest",
          },
        ]
      : []
  );

  const [initialRoleIds] = useState(props.assignedRoleIds ?? []);
  const allRoleItems = (roles ?? []).map((role) => ({
    type: "role" as const,
    role,
    id: role.id + "-role",
  }));
  const selectedRoleItems = allRoleItems.filter((r) =>
    initialRoleIds.includes(r.role.id)
  );
  const unselectedRoleItems = allRoleItems.filter(
    (r) => !initialRoleIds.includes(r.role.id)
  );

  const list = [
    ...selectedRoleItems,
    ...(assignedShoedOnTop.length > 0
      ? [
          ...assignedShoedOnTop,
          ...[
            {
              type: "header" as const,
              id: "header-working",
              title: "Working Today",
            },
          ],
        ]
      : []),
    ...unselectedRoleItems,
    ...rest,
  ];

  const onPress = useEvent((employeeId: string) => {
    onPressInHaptic();
    setAssignEmployeeIds(
      assignedEmployeeIds.includes(employeeId)
        ? assignedEmployeeIds.filter((id) => id !== employeeId)
        : [...assignedEmployeeIds, employeeId]
    );
  });

  const { assignedRoleIds = [], setAssignRoleIds } = props;
  const onPressRole = useEvent((roleId: string) => {
    if (!setAssignRoleIds) return;
    onPressInHaptic();
    setAssignRoleIds(
      assignedRoleIds.includes(roleId)
        ? assignedRoleIds.filter((id) => id !== roleId)
        : [...assignedRoleIds, roleId]
    );
  });

  type LI = (typeof list)[number];
  const renderItem = useCallback(
    ({ item }: { item: LI }) => {
      if (item.type === "header") {
        return <Box spaceVertical="medium" color="surface"></Box>;
      }
      if (item.type === "role") {
        return (
          <RoleListItem
            disabled={props.disabled ?? false}
            locked={props.rolesLocked}
            role={item.role}
            selected={assignedRoleIds.includes(item.role.id)}
            onPress={onPressRole}
          />
        );
      }
      return (
        <EmployeeItem
          disabled={props.disabled ?? false}
          employee={item.employee}
          selected={assignedEmployeeIds.includes(item.employee.id)}
          onPress={onPress}
        />
      );
    },
    [assignedEmployeeIds, assignedRoleIds, onPress, onPressRole, props.disabled, props.rolesLocked]
  );

  const keyExtractor = useCallback((item: LI) => {
    if (item.type === "header") return item.id;
    return item.id;
  }, []);

  return (
    <Dialog
      visible={props.visible}
      onClose={props.onClose}
      height={"80%"}
      maxHeight={600}
      title={
        <FormattedMessage
          description=" Assignees Title"
          defaultMessage="Assignees"
        />
      }
    >
      <Box style={{ flex: 1 }}>
        <FlashList
          data={list}
          renderItem={renderItem}
          keyExtractor={keyExtractor}
          ItemSeparatorComponent={AHSeparator}
          onEndReached={loadMore}
          onEndReachedThreshold={0.5}
          getItemType={(item) => item.type}
          maintainVisibleContentPosition={{
            autoscrollToTopThreshold: 0,
          }}
        />
      </Box>
    </Dialog>
  );
}

const EmployeeItem = memo(EmployeeItemC);
function EmployeeItemC(props: {
  employee: TVAssigneesPopover_EmployeeItem$key;
  selected: boolean;
  onPress: (employeeId: string) => void;
  disabled: boolean;
}) {
  const employee = useFragment(
    graphql`
      fragment TVAssigneesPopover_EmployeeItem on Employee {
        id
        name

        ...Avatar_employee
      }
    `,
    props.employee
  );

  return (
    <ListItem
      disabled={props.disabled}
      onPress={() => props.onPress(employee.id)}
      left={<EmployeeAvatar employee={employee} size="large" />}
      right={<ListItem.SelectIndicator selected={props.selected} />}
    >
      <ListItem.Title>{employee.name}</ListItem.Title>
    </ListItem>
  );
}
